import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { authAPI } from '../utils/api';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await authAPI.forgotPassword(email.trim().toLowerCase());
      setSubmitted(true);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <div className="mb-10 text-center">
          <img
            src="https://customer-assets-4nw71qhi.emergentagent.net/job_83ef92c5-3c5d-4a35-b89a-364f4b1c35a2/artifacts/htwqujap_1000X1000.png"
            alt="Ami Video"
            className="h-24 mx-auto mb-6 dark:brightness-0 dark:invert"
          />
          <h1 className="text-3xl font-bold tracking-tight mb-2">Ami Video</h1>
          <p className="text-xs text-muted-foreground tracking-[0.25em] uppercase">
            Photography is Poetry
          </p>
        </div>

        {submitted ? (
          <div className="text-center space-y-6" data-testid="forgot-password-success">
            <h2 className="text-2xl font-semibold tracking-tight">Check your email</h2>
            <p className="text-sm text-muted-foreground">
              If an account exists for <span className="text-foreground font-medium">{email}</span>, we've sent a link to reset your password. The link expires in 30 minutes.
            </p>
            <Link
              to="/login"
              className="inline-flex items-center gap-2 text-sm text-foreground hover:opacity-70 transition-opacity"
              data-testid="back-to-login-link"
            >
              <ArrowLeft size={14} />
              Back to sign in
            </Link>
          </div>
        ) : (
          <>
            <div className="mb-8 text-center">
              <h2 className="text-2xl font-semibold tracking-tight mb-1">Forgot your password?</h2>
              <p className="text-sm text-muted-foreground">Enter your email and we'll send you a reset link</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div className="bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 rounded-2xl text-sm" data-testid="forgot-password-error">
                  {error}
                </div>
              )}

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-muted-foreground mb-1.5">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3.5 rounded-2xl bg-secondary border border-transparent text-foreground focus:outline-none focus:bg-card focus:border-black/20 focus:ring-2 focus:ring-black/10 transition-all"
                  placeholder="Enter your email"
                  required
                  data-testid="forgot-password-email-input"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-primary text-primary-foreground px-6 py-3.5 rounded-full font-medium hover:bg-black/90 transition-all active:scale-[0.98] disabled:opacity-50"
                data-testid="forgot-password-submit-button"
              >
                {loading ? 'Sending…' : 'Send Reset Link'}
              </button>

              <div className="text-center">
                <Link
                  to="/login"
                  className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="back-to-login-link"
                >
                  <ArrowLeft size={14} />
                  Back to sign in
                </Link>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default ForgotPassword;
